/* ============================================================
   Pure helper for EditPost's Schedule tab "Save as draft" / "Save draft"
   button (bug 2026-09-10, owner repro on staging): the button was gated on
   `disabled={saving || !isDirty}`, where `isDirty` only reflects
   caption/hashtags/scheduledAt edits. Choosing "Save as draft" on a
   currently SCHEDULED post is itself the action — EditPost's handleSave
   calls api.scheduler.unschedule(scheduledPostId) to cancel the schedule
   and drop the post back to drafts — but with zero text edits `isDirty`
   stayed false and the button stayed disabled forever, so a user could
   not take a scheduled post back to drafts without also editing it.

   Keep this pure/side-effect-free so it can be unit tested without
   mounting EditPost.
   ============================================================ */

/**
 * Whether the draft-mode Save button should be enabled.
 *
 * @param {object} params
 * @param {boolean} params.isDirty - caption/hashtags/scheduledAt changed
 *   from the loaded originals (EditPost.jsx ~line 835).
 * @param {boolean} params.isDraft - the loaded post's content status is
 *   already 'draft' or 'ready' (EditPost.jsx ~line 911). Already-a-draft
 *   posts have nothing to unschedule, so unschedule-only enabling doesn't
 *   apply to them.
 * @param {string} params.scheduleMode - the Schedule-tab radio value:
 *   'now' | 'scheduled' | 'draft'.
 * @param {boolean} params.hasScheduledPost - whether the post currently has
 *   an active scheduled_post (raw?.publishSummary?.scheduledPostId).
 * @returns {boolean}
 */
export function canSaveAsDraft({ isDirty, isDraft, scheduleMode, hasScheduledPost }) {
  if (isDirty) return true;
  // No text/schedule edits — only a real action if the user picked "Save as
  // draft" on a post that is NOT already a draft AND actually has a
  // schedule to cancel. Clicking Save in that state performs unschedule().
  return scheduleMode === 'draft' && !isDraft && Boolean(hasScheduledPost);
}
